import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Anchor, Box, Button } from '@mantine/core';

import { useGetOrganisation } from 'api/organisations';
import { DYNAMIC_URLS } from 'consts';
import type { BoardType } from 'types';

import { BoardCreateModal } from './BoardCreateModal';

type Props = {
  boards: BoardType[];
  selectedBoard: BoardType;
};

export const BoardsSidebar = ({ boards, selectedBoard }: Props) => {
  const { organisationId, settingsTabKey } = useParams();
  if (!organisationId || !settingsTabKey) throw new Error('Missing URL params');

  const { data: organisation } = useGetOrganisation({
    pathParams: { organisationId },
  });
  const { isAdministrator } = organisation || {};

  const [createModalOpened, setCreateModalOpened] = useState(false);

  const { t } = useTranslation('organisations');

  return (
    <>
      {isAdministrator && (
        <Button color="green" onClick={() => setCreateModalOpened(true)}>
          {t('organisationView.boards.create.button')}
        </Button>
      )}
      <BoardCreateModal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
      />
      <Box
        sx={theme => ({
          width: 200,
          paddingTop: isAdministrator ? theme.other.spacing(2) : 0,
        })}
      >
        {boards.map(board => {
          const isSelected =
            board.id.toString() === selectedBoard.id.toString();

          return (
            <Anchor
              key={board.id}
              component={Link}
              to={DYNAMIC_URLS.organisationBoardSettings({
                organisationId,
                boardId: board.id.toString(),
                settingsTabKey,
              })}
              sx={theme => ({
                display: 'block',
                padding: theme.other.spacing(2),
                borderRadius: theme.radius.sm,
                background: isSelected ? theme.colors.violet[1] : undefined,
                color: isSelected ? theme.colors.violet[0] : 'initial',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'none',
                },
              })}
            >
              {board.name}
            </Anchor>
          );
        })}
      </Box>
    </>
  );
};
